import React from 'react';
import {
  Check,
  Coffee,
  Dumbbell,
  Pause,
  Play,
  RotateCcw,
  Square,
  Timer as TimerIcon,
} from 'lucide-react';
import {brand, brandFont} from '../../brand';
import {TickRing} from './TickRing';

export type TimerPhase = 'ready' | 'running' | 'completed';

export type AppTimerDialProps = {
  type: string;
  progress: number;
  remainingSeconds: number;
  rounds: number;
  totalRounds: number;
  restsDone: number;
  totalRests: number;
  totalSecondsLabel: string;
  phase: TimerPhase;
  size?: number;
  showActions?: boolean;
  ringColor?: string;
};

const formatClock = (s: number) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
};

export const AppTimerDial: React.FC<AppTimerDialProps> = ({
  type,
  progress,
  remainingSeconds,
  rounds,
  totalRounds,
  restsDone,
  totalRests,
  totalSecondsLabel,
  phase,
  size = 380,
  showActions = true,
  ringColor = brand.primary,
}) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 28,
      padding: '20px 24px 8px',
    }}
  >
    <div
      style={{
        fontFamily: brandFont,
        color: brand.textPrimary,
        fontSize: 30,
        fontWeight: 800,
        letterSpacing: 5,
      }}
    >
      {type}
    </div>

    <Dial
      progress={progress}
      seconds={remainingSeconds}
      rounds={rounds}
      totalRounds={totalRounds}
      restsDone={restsDone}
      totalRests={totalRests}
      totalSecondsLabel={totalSecondsLabel}
      size={size}
      ringColor={ringColor}
      phase={phase}
    />

    {showActions ? <AppTimerActions phase={phase} /> : null}
  </div>
);

export const AppTimerActions: React.FC<{phase: TimerPhase}> = ({phase}) => {
  const center = (() => {
    if (phase === 'completed')
      return <Check size={42} color={brand.textPrimary} strokeWidth={2.6} />;
    if (phase === 'ready')
      return (
        <Play
          size={42}
          color={brand.textPrimary}
          strokeWidth={2.6}
          fill={brand.textPrimary}
        />
      );
    return (
      <Pause
        size={42}
        color={brand.textPrimary}
        strokeWidth={2.6}
        fill={brand.textPrimary}
      />
    );
  })();
  const centerActive = phase !== 'ready';

  return (
    <div style={{display: 'flex', gap: 36, alignItems: 'center'}}>
      <CircleButton size={64} bg="rgba(255,255,255,0.06)">
        <RotateCcw size={26} color="rgba(255,255,255,0.78)" strokeWidth={2.4} />
      </CircleButton>
      <CircleButton size={92} bg={brand.primary} glow={centerActive}>
        {center}
      </CircleButton>
      <CircleButton size={64} bg="rgba(255,255,255,0.06)">
        <Square
          size={22}
          color={brand.timerStop}
          strokeWidth={2.6}
          fill={brand.timerStop}
        />
      </CircleButton>
    </div>
  );
};

const Dial: React.FC<{
  progress: number;
  seconds: number;
  rounds: number;
  totalRounds: number;
  restsDone: number;
  totalRests: number;
  totalSecondsLabel: string;
  size: number;
  ringColor: string;
  phase: TimerPhase;
}> = ({
  progress,
  seconds,
  rounds,
  totalRounds,
  restsDone,
  totalRests,
  totalSecondsLabel,
  size,
  ringColor,
  phase,
}) => {
  const muted = 'rgba(255,255,255,0.5)';

  return (
    <div style={{position: 'relative', width: size, height: size}}>
      <TickRing size={size} progress={progress} color={ringColor} />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 14,
        }}
      >
        <IconLabel
          icon={<TimerIcon size={22} color={muted} strokeWidth={2.4} />}
          label={totalSecondsLabel}
          color={muted}
        />
        {phase === 'completed' ? (
          <Check size={Math.floor(size * 0.36)} color={ringColor} strokeWidth={3.2} />
        ) : (
          <div
            style={{
              fontFamily: brandFont,
              fontSize: Math.floor(size * 0.25),
              color: brand.textPrimary,
              fontWeight: 800,
              letterSpacing: -1.8,
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1,
            }}
          >
            {formatClock(seconds)}
          </div>
        )}
        <div style={{display: 'flex', gap: 28}}>
          <IconLabel
            icon={<Dumbbell size={22} color={muted} strokeWidth={2.4} />}
            label={`${rounds}/${totalRounds}`}
            color={muted}
          />
          <IconLabel
            icon={<Coffee size={22} color={muted} strokeWidth={2.4} />}
            label={`${restsDone}/${totalRests}`}
            color={muted}
          />
        </div>
      </div>
    </div>
  );
};

const IconLabel: React.FC<{icon: React.ReactNode; label: string; color: string}> = ({
  icon,
  label,
  color,
}) => (
  <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
    {icon}
    <span
      style={{
        fontFamily: brandFont,
        color,
        fontSize: 24,
        fontWeight: 700,
        letterSpacing: 0.2,
      }}
    >
      {label}
    </span>
  </div>
);

const CircleButton: React.FC<{
  size: number;
  bg: string;
  glow?: boolean;
  children: React.ReactNode;
}> = ({size, bg, glow, children}) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: '50%',
      background: bg,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      boxShadow: glow ? `0 0 30px ${brand.primary}80` : undefined,
    }}
  >
    {children}
  </div>
);
